/**
 * DanaVoice - Dashboard Layout Component
 *
 * Main layout wrapper for authenticated pages with navigation.
 * Integrates Neon Auth (Stack Auth) for user info and logout.
 * Includes language toggle, location access check, and admin nav filtering.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useUser } from '@stackframe/react';
import { stackClientApp } from '../../stack';
import { useToast } from '../../contexts/ToastContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPusherInstance } from '../../services/pusher-client';
import { getUserProfile, syncStackAuthUser } from '../../services/stack-auth-sync';
import type { TranslationKey } from '../../i18n/translations';

/**
 * Navigation Items — nameKey references i18n translations
 */
const navigationItems = [
  { nameKey: 'nav.dashboard' as TranslationKey, path: '/dashboard', icon: '📊' },
  { nameKey: 'nav.analytics' as TranslationKey, path: '/analytics', icon: '📈' },
  { nameKey: 'nav.callLogs' as TranslationKey, path: '/call-logs', icon: '📞' },
  { nameKey: 'nav.staff' as TranslationKey, path: '/staff', icon: '👨‍💼', adminOnly: true },
  { nameKey: 'nav.settings' as TranslationKey, path: '/settings', icon: '⚙️' },
];

const CURRENT_LOCATION = import.meta.env.VITE_LOCATION || 'dana';

/**
 * Dashboard Layout Component
 *
 * Features:
 * - Side navigation (Staff hidden for non-admins)
 * - Language toggle (DE/EN)
 * - Location access check (staff can only see assigned locations)
 * - User profile display
 * - Logout functionality
 */
export function DashboardLayout() {
  const navigate = useNavigate();
  const user = useUser({ or: 'redirect' });
  const { showToast } = useToast();
  const { t, language, toggleLanguage } = useLanguage();

  // User profile state for access control
  const [userRole, setUserRole] = useState<string | null>(null);
  const [accessState, setAccessState] = useState<'loading' | 'allowed' | 'denied'>('loading');
  const accessCheckDone = useRef(false);

  // Pusher channel reference (singleton instance managed by service)
  const channelRef = useRef<any>(null);

  /**
   * Check user's role and location access on mount (runs once).
   * If profile not found, try syncing user first then retry.
   */
  useEffect(() => {
    if (accessCheckDone.current || !user) return;
    accessCheckDone.current = true;

    const checkAccess = async () => {
      try {
        let profile: any = null;

        // First try to get existing profile
        try {
          profile = await getUserProfile();
        } catch {
          // 401 or other error — token may not be ready or user not synced yet
        }

        // If profile not found, try syncing user (links Neon Auth ID to staff record)
        if (!profile) {
          try {
            const syncResult = await syncStackAuthUser(
              user.id,
              user.primaryEmail || '',
              user.displayName || undefined
            );
            if (syncResult) {
              profile = { role: syncResult.role, locations: syncResult.locations };
            }
          } catch {
            // Sync failed — user may not be in staff_members table
          }
        }

        if (profile) {
          setUserRole(profile.role);
          if (profile.role === 'administrator') {
            setAccessState('allowed');
          } else if (profile.locations?.includes(CURRENT_LOCATION)) {
            setAccessState('allowed');
          } else {
            setAccessState('denied');
          }
        } else {
          // No staff record found — deny access (user not whitelisted)
          setAccessState('denied');
        }
      } catch {
        // If everything fails, deny access for safety
        setAccessState('denied');
      }
    };
    checkAccess();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Global Pusher subscription for toast notifications on all pages
   * Uses SINGLETON instance to prevent multiple WebSocket connections
   */
  useEffect(() => {
    console.log('[LAYOUT] Subscribing to Pusher...');

    // Get the singleton Pusher instance
    const pusher = getPusherInstance();
    if (!pusher) {
      console.error('[LAYOUT] Failed to get Pusher instance');
      return;
    }

    // Subscribe to dashboard-updates channel
    const channel = pusher.subscribe('dashboard-updates');
    channelRef.current = channel;

    // Bind to events directly (like Serena dashboard)
    channel.bind('call_started', (data: any) => {
      console.log('[LAYOUT] New call started:', data);
      showToast({
        type: 'info',
        title: t('toast.newCall'),
        message: data.caller_phone
          ? t('toast.incomingCallFrom', { phone: data.caller_phone })
          : t('toast.newIncomingCall'),
        action: {
          label: t('common.view'),
          onClick: () => navigate('/call-logs')
        }
      });
    });

    channel.bind('call_updated', (data: any) => {
      console.log('[LAYOUT] Call updated:', data);
      // No toast for call updates to avoid spam
    });

    channel.bind('call_ended', (data: any) => {
      console.log('[LAYOUT] Call ended:', data);
      showToast({
        type: 'success',
        title: t('toast.callEnded'),
        message: data.caller_phone
          ? t('toast.callEndedFrom', { phone: data.caller_phone })
          : t('toast.callHasEnded'),
        action: {
          label: t('common.viewDetails'),
          onClick: () => navigate('/call-logs')
        }
      });
    });

    channel.bind('notification_created', (data: any) => {
      console.log('[LAYOUT] Notification created:', data);
      if (data.notification) {
        const notification = data.notification;
        showToast({
          type: notification.priority === 'urgent' ? 'error' : 'info',
          title: notification.title,
          message: notification.message,
          action: {
            label: t('common.view'),
            onClick: () => navigate('/dashboard')
          }
        });
      }
    });

    // Cleanup - only unsubscribe, don't disconnect (singleton instance)
    return () => {
      if (channelRef.current) {
        pusher.unsubscribe('dashboard-updates');
      }
    };
  }, []); // Empty deps - only run once on mount

  /**
   * Handle Logout
   */
  const handleLogout = async () => {
    await stackClientApp.signOut();
    navigate('/handler/sign-in', { replace: true });
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-dana-primary"></div>
      </div>
    );
  }

  // Filter nav items: hide admin-only items for non-admins
  const visibleNavItems = navigationItems.filter(item => {
    if (item.adminOnly && userRole !== 'administrator') {
      return false;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar — h-screen + sticky keeps it pinned and scrollable internally */}
      <aside className="w-64 bg-white border-r border-gray-200 flex flex-col h-screen sticky top-0 shrink-0">
        {/* Logo */}
        <div className="p-4 border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-900">
            {t('common.appName')}
          </h1>
          <p className="text-xs text-gray-500 mt-1">{t('common.virtualReceptionist')}</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {visibleNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-indigo-50 text-indigo-600'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`
              }
            >
              <span className="text-lg">{item.icon}</span>
              {t(item.nameKey)}
            </NavLink>
          ))}
        </nav>

        {/* User Profile */}
        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
              <span className="text-indigo-600 font-semibold text-sm">
                {user.displayName?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user.displayName || user.primaryEmail || t('common.user')}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {user.primaryEmail}
              </p>
            </div>
          </div>
          {/* Language Toggle */}
          <button
            type="button"
            onClick={toggleLanguage}
            className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 mb-2 flex items-center justify-center gap-2"
          >
            {language === 'en' ? '🇩🇪' : '🇬🇧'} {t('language.toggle')}
          </button>
          {/* Logout */}
          <button
            type="button"
            onClick={handleLogout}
            className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            {t('common.logout')}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {accessState === 'loading' ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : accessState === 'denied' ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center max-w-md px-6">
              <div className="text-6xl mb-4">🔒</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('access.restricted')}</h2>
              <p className="text-gray-600 mb-2">{t('access.notAssigned')}</p>
              <p className="text-gray-500 text-sm">{t('access.contactAdmin')}</p>
            </div>
          </div>
        ) : (
          <Outlet />
        )}
      </main>
    </div>
  );
}
